'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Trash2 } from 'lucide-react';
import type { BookingType } from '@/lib/services/guide-profile-service';

interface BookingTypeEditorProps {
  bookingType: BookingType;
  onChange: (updated: BookingType) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export default function BookingTypeEditor({ bookingType, onChange, onRemove, canRemove }: BookingTypeEditorProps) {
  const [bt, setBt] = useState(bookingType);

  function update(patch: Partial<BookingType>) {
    const updated = { ...bt, ...patch };
    if (patch.is_free !== undefined) {
      updated.price_cents = patch.is_free ? 0 : updated.price_cents;
    }
    setBt(updated);
    onChange(updated);
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-start p-3 rounded-lg border bg-card">
      <div className="flex-1 space-y-2">
        <div>
          <Label className="text-xs text-muted-foreground">Label</Label>
          <Input
            value={bt.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="e.g. Intro Chat"
            className="h-8"
          />
        </div>
      </div>
      <div className="w-24 space-y-2">
        <Label className="text-xs text-muted-foreground">Duration</Label>
        <Input
          type="number"
          value={bt.duration_minutes}
          onChange={(e) => update({ duration_minutes: parseInt(e.target.value) || 15 })}
          min={5}
          max={180}
          className="h-8"
        />
      </div>
      <div className="w-28 space-y-2">
        <Label className="text-xs text-muted-foreground">Price ($)</Label>
        <Input
          type="number"
          value={bt.is_free ? 0 : bt.price_cents / 100}
          onChange={(e) => update({ price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
          disabled={bt.is_free}
          min={0}
          step={5}
          className="h-8"
        />
      </div>
      <div className="flex flex-col items-center gap-1 pt-5">
        <div className="flex items-center gap-2">
          <Switch
            checked={bt.is_free}
            onCheckedChange={(checked) => update({ is_free: checked })}
          />
          <span className="text-xs text-muted-foreground">Free</span>
        </div>
      </div>
      {canRemove && (
        <Button variant="ghost" size="icon" className="mt-5" onClick={onRemove}>
          <Trash2 className="w-4 h-4 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
